<template>
  <!-- Invisible anchor positioned under the invoking checkbox; the status
       dropdown (a Tippy teleported to #theme) opens against it. -->
  <div class="status-picker-anchor" :style="anchorStyle">
    <GraphSelect
      v-if="target"
      :key="invocation"
      ref="selectEl"
      state="inline"
      :name="target.fieldName"
      :type="target.selectType"
      :data="target.value"
      :show-placeholder="false"
      :show-clear-button="false"
    />
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  nextTick,
  ref,
  shallowRef,
  watch,
} from "vue";

import type { GeneralizedTask, WorkspaceId } from "@ctrl/controller-rpc";

import GraphSelect from "@/components/graph/properties/GraphSelect.vue";
import * as inject from "@/components/inject";
import {
  taskStatusSelectTarget,
  type TaskStatusSelectTarget,
} from "@/components/task/status";

/**
 * App-wide status picker (mounted once by Routine.vue, like
 * `AssigneePicker`): `showPicker(triggerEl, task)` opens the status
 * dropdown of a bound task whose Status select was extended beyond the base
 * provisioned statuses, under `triggerEl` (the long-pressed completion
 * checkbox). It reuses `GraphSelect` (the same editor as the object page's
 * Status property), whose autosave writes the selection to the task's bound
 * row. No-ops when the picker does not apply (see `taskStatusSelectTarget`).
 */
export default defineComponent({
  components: { GraphSelect },
  setup() {
    const controller = inject.controller();
    const profile = inject.profile();
    const target = shallowRef<TaskStatusSelectTarget | null>(null);
    // Bumped on every `showPicker` so the `GraphSelect` remounts with the
    // freshly resolved task data.
    const invocation = ref(0);
    const anchorStyle = ref<Record<string, string>>({});
    const selectEl = ref<InstanceType<typeof GraphSelect>>();
    const pendingOpen = ref(false);

    // The graph write context `GraphSelect` resolves through inject (it has
    // no objectId/database props): its autosave path writes to the bound
    // row's status cell, and the workspace scopes the select order/color
    // configuration.
    inject.objectId(computed(() => target.value?.rowId));
    inject.database(computed(() => target.value?.database));
    inject.autosave(ref(true));
    inject.currentWorkspaceId(
      computed<WorkspaceId>(
        () =>
          target.value?.database.workspace ??
          profile.value.user.personal.workspaceId
      )
    );

    // Open the dropdown once the remounted `GraphSelect` is there.
    watch(
      () => (pendingOpen.value ? selectEl.value : undefined),
      async (select) => {
        if (!select) return;
        pendingOpen.value = false;
        await nextTick();
        // Re-read after the tick: the instance may have been replaced.
        const current = selectEl.value;
        if (current && !current.isPopupOpen()) current.togglePopup();
      }
    );

    const showPicker = async (
      triggerEl: HTMLElement | undefined,
      task: GeneralizedTask
    ) => {
      const resolved = await taskStatusSelectTarget(controller, task);
      // Hosts only enable the long press when the status select is
      // extended, so an unresolved target is a race (e.g. the type was just
      // edited): nothing to open.
      if (!resolved) return;
      const rect = triggerEl?.getBoundingClientRect();
      // Tear down any previous `GraphSelect` before arming `pendingOpen`:
      // on repeat invocations the watch above would otherwise fire against
      // the previous instance — right before the key change destroys it —
      // and its `togglePopup()` would no-op on a destroyed tippy, eating
      // the invocation.
      pendingOpen.value = false;
      target.value = null;
      await nextTick();
      anchorStyle.value = rect
        ? { top: `${rect.bottom}px`, left: `${rect.left}px` }
        : { top: "50%", left: "50%" };
      invocation.value += 1;
      target.value = resolved;
      pendingOpen.value = true;
    };

    return {
      anchorStyle,
      invocation,
      selectEl,
      showPicker,
      target,
    };
  },
});
</script>

<style scoped lang="scss">
.status-picker-anchor {
  position: fixed;
  z-index: $z-index-modal;
  width: 0;
  height: 0;
  pointer-events: none;
  overflow: hidden;
}
</style>
